import { Suspense } from 'react'
import type { TaskEventReplayProps } from './TaskEventReplayImpl'
import { lazyWithRetry } from '../lib/lazyWithRetry'
import ChunkErrorBoundary from './ChunkErrorBoundary'
import './TaskEventReplay.css'

// rrweb (the Replayer + its styles) is ~heavy, so the implementation is code-split into its own
// chunk and only fetched when a replay is actually rendered (a journey/exploration row expands).
// The `import type` above is erased at compile time, so it doesn't pull the impl into the main
// bundle — only the lazy() dynamic import does, in a separate chunk. lazyWithRetry + the error
// boundary keep a stale-chunk 404 (after a deploy) from blanking the page.
const TaskEventReplayImpl = lazyWithRetry(() => import('./TaskEventReplayImpl'))

export type { ReplaySource } from './TaskEventReplayImpl'

// While the rrweb chunk loads (or fails), reserve the shape the real player settles into so swapping
// it in doesn't shift the layout. That is BOTH parts of the player: the stage (16:9 by default, or
// height-filling in fitHeight mode, where the slot is bounded by its container) and the transport bar
// underneath it — reserving the stage alone left the bar's ~53px to arrive later and push down
// whatever follows. Geometry stays inline so the lazy fallback is self-contained; the small shared
// stylesheet supplies only the loading shimmer and mirrors the real player's visual transition.
const rootStyle = (fitHeight?: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
  ...(fitHeight ? { height: '100%' } : {}),
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  background: 'var(--color-surface)',
  boxSizing: 'border-box',
})

const stageStyle = (fitHeight?: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Containing block for the shimmer band (`.replay-loading-shimmer::before`, inset 0).
  position: 'relative',
  ...(fitHeight ? { flex: '1 1 auto', minHeight: 0 } : { aspectRatio: '16 / 9' }),
  color: 'var(--color-text-secondary, #9ca3af)',
  fontSize: 13,
  textAlign: 'center',
  padding: 16,
  boxSizing: 'border-box',
})

// 28px controls + 8px padding above and below + the rule along its top.
const barStyle: React.CSSProperties = {
  flex: '0 0 auto',
  height: 45,
  borderTop: '1px solid var(--color-border)',
}

function ReplayFallback({ fitHeight, loading = false, children }: { fitHeight?: boolean; loading?: boolean; children: React.ReactNode }) {
  return (
    <div style={rootStyle(fitHeight)}>
      <div style={stageStyle(fitHeight)} className={loading ? 'replay-loading-shimmer' : undefined}>
        <span className="replay-loading-shimmer__content">{children}</span>
      </div>
      <div style={barStyle} aria-hidden />
    </div>
  )
}

export default function TaskEventReplay(props: TaskEventReplayProps) {
  return (
    <ChunkErrorBoundary
      eventTag="replay-unavailable"
      fallback={<ReplayFallback fitHeight={props.fitHeight}>Replay unavailable — reload the page to retry.</ReplayFallback>}
    >
      <Suspense fallback={<ReplayFallback fitHeight={props.fitHeight} loading>Loading replay…</ReplayFallback>}>
        <TaskEventReplayImpl {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  )
}
